import { useAccount, useHeartbeatGrid, useHeartbeatRuns } from '../../../services/queries';
import { ACTIVE } from './pageState';
import { useHeartbeatRun } from './useHeartbeatRun';
import { useVoices } from './useVoices';

/**
 * One pulse, in full - the data behind level 3 of the revised structure.
 *
 * The account page previously rendered the newest terminal run and nothing
 * else, so every earlier pulse was listed in the history and unreachable. This
 * hook takes the runId from the route instead of picking one, which is what
 * makes a past pulse openable.
 *
 * `previous` is the run immediately before this one for the same account, so
 * the mood tiles can still show a delta - against the right comparison, rather
 * than against whatever happened to be second-newest overall.
 */
export function useHeartbeatRunDetail(accountId, runId, revision) {
	const accountQ = useAccount(accountId);
	const runsQ = useHeartbeatRuns(accountId);
	const runs = runsQ.data || [];

	// Live for a run still in flight; a settled run just reads its snapshot.
	const live = useHeartbeatRun(runId, accountId, revision);
	const run = live.run;

	const terminal = runs.filter((r) => !ACTIVE.includes(r.status));
	const index = terminal.findIndex((r) => r._id === runId);
	const previous = index >= 0 ? terminal[index + 1] || null : null;

	const pinnedRevision = revision || run?.readingRevision;
	const gridQ = useHeartbeatGrid(accountId, runId ? { runId, ...(pinnedRevision ? { revision: pinnedRevision } : {}) } : undefined, { enabled: !!runId && !!run });
	const prevGridQ = useHeartbeatGrid(
		accountId,
		previous ? { runId: previous._id } : undefined,
		{ enabled: !!previous }
	);

	const voicesState = useVoices(accountId, runId, pinnedRevision);

	return {
		account: accountQ.data || null,
		accountLoading: accountQ.isLoading,
		run,
		runLoading: live.isLoading,
		runError: live.error,
		isActive: live.isActive,
		runs,
		previous,
		grid: gridQ.data?.rows || [],
		tiles: gridQ.data?.tiles || {},
		previousTiles: prevGridQ.data?.tiles || null,
		gridLoading: gridQ.isLoading,
		...voicesState,
	};
}
